import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana, canUseRounds, isOwnerOrManager } from "../../helpers/karkhanaEnabled";
import { changeSareeNumber, editLoomEntry, fixSareeWorkType, getSareeHistory, listKarkhanaWorkTypes } from "../../services/karkhanaApi";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { readRoster, refreshRoster } from "./punches/roster";
import "../gi-registry/gi-registry.scss";
import { hours } from "./punches/punchModel";
import { fmtDay, partName, rupees } from "./rates/rateModel";
import { gajGirah } from "./rounds/sareeLength";
import PageHeader from "./ui/PageHeader";
import "./ui/karkhana-history.scss";
import "./workers/karkhana-workers.scss";
import { useKT } from "./i18n/kt";
import { askReason, confirmDialog } from "./ui/dialog";
import SareeCostingCard from "./costing/SareeCostingCard";
import SareeStartChange from "./sarees/SareeStartChange";
import ChangeList from "./sarees/ChangeList";
import { cleanSareeNumber } from "./sarees/sareeEditModel";
import EntryChangeDialog, { ChangeTarget } from "./sarees/EntryChangeDialog";
import { HistoryEntry, isRemoved } from "./sarees/entryChangeModel";
import { kp } from "./karkhanaPaths";
import { useKarkhanaTitle } from "./ui/addressState";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

type Edit = { id: string; date: string; weaverId: string; helperId: string; inches: string; workTypeId: string; ladkaSool: boolean; soolTouched: boolean };

const STATUS: Record<string, string> = { ACTIVE: "On the loom", COMPLETED: "Complete", STOPPED: "Stopped", CUT: "Cut" };

/**
 * One saree like the old sheet shows it — who wove which part on which day — plus what it has
 * cost so far (studio staff only): each weaver's earnings, hours tagged to it and job work.
 * Wrong parts entered over several days can be ticked and corrected together. The owner and managers
 * can also move an entry to another day or saree, remove entries (struck through, with the reason)
 * and restore them; each change is previewed first.
 */
const KarkhanaSareeHistoryPage = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const auth = readAuth();
  const role = auth?.role;
  const orgId: string = auth?.organizationResponse?._id || "org";
  const [roster, setRoster] = useState(() => readRoster(orgId));
  // Supervisors' quick fix of their own entry (the old PUT); owners and managers use `change`.
  const [edit, setEdit] = useState<Edit | null>(null);
  const [change, setChange] = useState<ChangeTarget | null>(null);
  const [data, setData] = useState<any>(null);
  const [types, setTypes] = useState<any[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [fixTo, setFixTo] = useState("");
  const [busy, setBusy] = useState(false);
  // Correcting the saree's number (owner or manager): null when not editing.
  const [numberText, setNumberText] = useState<string | null>(null);
  const [numberNote, setNumberNote] = useState("");

  const load = useCallback(async () => {
    try {
      const res: any = await getSareeHistory(id);
      if (res?.success) setData(res.data);
      else notifications({ title: t("Error"), message: t(res?.message || "Could not load"), type: "danger" });
    } catch {
      notifications({ title: t("No connection"), message: t("This needs a connection. Try again in a moment."), type: "warning" });
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canUseRounds(role)) {
      history.replace(kp.home());
      return;
    }
    load();
    refreshRoster(orgId).then(setRoster).catch(() => undefined);
    listKarkhanaWorkTypes(0, 200)
      .then((res: any) => setTypes((res?.data?.docs || []).filter((w: any) => w.active !== false)))
      .catch(() => undefined);
  }, [role, history, load, orgId]);

  /** Save a corrected entry; the server checks it like a new one and works the pay out again. */
  const saveEdit = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      const res: any = await editLoomEntry(edit.id, {
        weaverId: edit.weaverId,
        helperId: edit.helperId,
        helperMode: edit.helperId ? "WITH_HELPER" : "ALONE",
        inches: edit.inches.trim(),
        workTypeId: edit.workTypeId,
        // Sent only when ticked or unticked here; otherwise the entry keeps what it had.
        ...(edit.soolTouched ? { ladkaSool: edit.ladkaSool } : {}),
      });
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({ title: t("Saved"), message: t("Entry corrected; pay worked out again."), type: "success" });
      setEdit(null);
      load();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  /** A typo in the number (118301 for 18301) is corrected here; entries and job work move with it. */
  const saveNumber = async () => {
    if (numberText === null || !numberText) return;
    setBusy(true);
    try {
      const res: any = await changeSareeNumber(id, numberText, numberNote.trim() || undefined);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({ title: t("Saved"), message: t("Saree {{old}} is now {{n}}", { old: res.data?.oldNumber, n: res.data?.sareeNumber }), type: "success" });
      setNumberText(null);
      setNumberNote("");
      load();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const days: any[] = useMemo(() => data?.days || [], [data]);
  const money = !!data?.showMoney;
  const s = data?.saree;
  useKarkhanaTitle(s ? `Saree ${s.sareeNumber}` : null);
  const tot = data?.totals || {};
  const other = data?.otherWork || {};

  const pickedEntries = days.filter((d) => picked.has(d.id));

  /** Remove the ticked entries: one reason, one batch, previewed before anything changes. */
  const removePicked = async () => {
    if (!pickedEntries.length) return;
    const why = await askReason({ title: t("Remove {{n}} entries? Say why:", { n: pickedEntries.length }) });
    if (why) setChange({ kind: "remove", entries: pickedEntries, reason: why });
  };

  const restore = async (d: HistoryEntry) => {
    const why = await askReason({ title: t("Bring back the entry for {{day}}? Say why:", { day: fmtDay(d.date) }) });
    if (why) setChange({ kind: "restore", entry: d, reason: why });
  };

  const toggle = (eid: string) =>
    setPicked((p) => {
      const n = new Set(p);
      n.has(eid) ? n.delete(eid) : n.add(eid);
      return n;
    });

  const applyFix = async () => {
    if (!fixTo || !picked.size) return;
    const to = types.find((w) => w.id === fixTo);
    if (!(await confirmDialog({ title: t("Change {{n}} entries to {{part}}? Their pay is worked out again.", { n: picked.size, part: to ? `${partName(to, lang, t)} (${to.code})` : "" }) }))) return;
    setBusy(true);
    try {
      const res: any = await fixSareeWorkType(id, Array.from(picked), fixTo);
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: t(res?.message || "Could not save"), type: "danger" });
        return;
      }
      notifications({ title: t("Saved"), message: t("{{n}} entries now {{code}}", { n: res.data.changed, code: res.data.workTypeCode }), type: "success" });
      setPicked(new Set());
      setFixTo("");
      load();
    } catch {
      notifications({ title: t("No connection"), message: t("Nothing was saved. Try again."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  if (!canUseRounds(role)) return null;
  // Start date and number corrections: the owner and managers (the server checks it too).
  const canCorrect = isOwnerOrManager(role);
  const isOwner = role === "ORGANIZATION_ADMIN";

  return (
    <div className="gi-registry-page kw-page kh-history">
      <PageHeader title={s ? `${t("Saree")} ${s.sareeNumber}` : t("Saree")} back={kp.sarees()} backLabel={t("Back")} />
      {!data ? <div className="kw-empty">{t("Loading…")}</div> : null}

      {s ? (
        <>
          <p className="kw-lede">
            {t("design")} <strong>{s.designNumber}</strong> · {t("Loom")}{" "}
            <Link to={kp.loom(s.loomId)}>{s.loomCode}</Link> · {fmtDay(s.startedOn)}
            {s.finishedOn ? ` – ${fmtDay(s.finishedOn)}` : ""} · {t(STATUS[s.status] || s.status)}
            {canCorrect && numberText === null ? (
              <>
                {" "}
                ·{" "}
                <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setNumberText(s.sareeNumber || "")}>
                  {t("Change number")}
                </button>
              </>
            ) : null}
          </p>
          {numberText !== null ? (
            <div className="kh-since">
              <label className="kr-label" htmlFor="kh-saree-no">
                {t("Saree number")}
              </label>
              <input
                id="kh-saree-no"
                className="kr-select"
                inputMode="numeric"
                value={numberText}
                onChange={(e) => setNumberText(cleanSareeNumber(e.target.value))}
                autoFocus
              />
              <p className="kw-muted kh-since__hint">
                {t("A lower number is fine if it is free. New sarees still get numbers after the highest ever given; {{old}} is never given again.", { old: s.sareeNumber })}
              </p>
              <label className="kr-label" htmlFor="kh-saree-no-note">
                {t("Why (optional)")}
              </label>
              <input id="kh-saree-no-note" className="kr-select" value={numberNote} maxLength={300} onChange={(e) => setNumberNote(e.target.value)} />
              <div className="kh-since__actions">
                <button type="button" className="kh-btn" disabled={busy} onClick={() => { setNumberText(null); setNumberNote(""); }}>
                  {t("Cancel")}
                </button>
                <button type="button" className="kh-btn is-primary" disabled={busy || !numberText || numberText === s.sareeNumber} onClick={saveNumber}>
                  {busy ? t("Saving…") : t("Save number")}
                </button>
              </div>
            </div>
          ) : null}
          {s.loomId ? <SareeStartChange pieceId={id} start={s.startedOn} canChange={canCorrect} onChanged={load} /> : null}

          <div className="kh-stats">
            <div>
              <span>{t("Woven")}</span>
              <strong>{gajGirah(Number(tot.sareeInches || 0))}</strong>
              <small>
                {tot.sareeInches} in · {tot.sareeMetres} m
              </small>
            </div>
            <div>
              <span>{t("Days on the loom")}</span>
              <strong>{tot.daysWorked}</strong>
            </div>
            {money ? (
              <>
                <div className="is-main">
                  <span>{t("Cost so far")}</span>
                  <strong>{rupees(tot.totalCost, 0)}</strong>
                  <small>
                    {t("weaving")} {rupees(tot.weavingPay, 0)} · {t("hours")} {rupees(tot.hoursPay, 0)} · {t("job work")} {rupees(tot.jobPay, 0)}
                  </small>
                </div>
              </>
            ) : null}
          </div>
          {money && canManageKarkhana(role) ? <SareeCostingCard pieceId={id} /> : null}
          {tot.waitingEntries || tot.unratedEntries ? (
            <p className="kw-muted">
              {tot.waitingEntries ? t("{{n}} entries wait for approval and aren't counted yet.", { n: tot.waitingEntries }) : ""}{" "}
              {tot.unratedEntries ? t("{{n}} entries have no approved rate.", { n: tot.unratedEntries }) : ""}
            </p>
          ) : null}

          <h3 className="kr-group-title">{t("Weavers on this saree")}</h3>
          <div className="kh-table">
            <div className="kh-table__row is-head">
              <span>{t("Weaver")}</span>
              <span className="num">{t("Days")}</span>
              <span className="num">{t("Inches")}</span>
              {money ? <span className="num">{t("Earned")}</span> : null}
            </div>
            {(data.weavers || []).map((w: any) => (
              <div key={w.id} className="kh-table__row">
                <span>
                  <span className="kw-code">{w.code}</span> {w.name}
                </span>
                <span className="num">{w.days}</span>
                <span className="num">{w.inches}</span>
                {money ? <span className="num">{rupees(w.earned)}</span> : null}
              </div>
            ))}
          </div>

          <h3 className="kr-group-title">{t("Day by day")}</h3>
          {picked.size ? (
            <div className="kh-fixbar">
              <span>{t("{{n}} selected", { n: picked.size })}</span>
              <select className="kr-select" value={fixTo} onChange={(e) => setFixTo(e.target.value)} aria-label={t("Correct part")}>
                <option value="">{t("Change the part to…")}</option>
                {types.map((w) => (
                  <option key={w.id} value={w.id}>
                    {partName(w, lang, t)} ({w.code})
                  </option>
                ))}
              </select>
              <button type="button" className="kh-btn is-primary" disabled={!fixTo || busy} onClick={applyFix}>
                {busy ? t("Saving…") : t("Change")}
              </button>
              {canCorrect ? (
                <button type="button" className="kh-btn is-danger" disabled={busy} onClick={removePicked}>
                  {pickedEntries.length === 1 ? t("Remove 1 entry") : t("Remove {{n}} entries", { n: pickedEntries.length })}
                </button>
              ) : null}
              <button type="button" className="kh-btn" onClick={() => setPicked(new Set())}>
                {t("Cancel")}
              </button>
            </div>
          ) : (
            <p className="kw-muted">
              {canCorrect
                ? t("Entered the wrong part on some days, or entries that shouldn't be there? Tick them to change the part or remove them together.")
                : t("Entered the wrong part on some days? Tick them and change the part together.")}
            </p>
          )}
          <div className="kh-table kh-table--days">
            <div className="kh-table__row is-head">
              <span aria-label={t("Select")} />
              <span>{t("Date")}</span>
              <span>{t("Part")}</span>
              <span className="num">{t("Inches")}</span>
              <span>{t("Weaver")}</span>
              <span>{t("Helper")}</span>
              {money ? <span className="num">₹</span> : null}
              <span aria-label={t("Edit")} />
            </div>
            {days.map((d) => {
              const gone = isRemoved(d);
              const Row: any = gone ? "div" : "label";
              return (
                <Row key={d.id} className={`kh-table__row${picked.has(d.id) ? " is-picked" : ""}${gone ? " is-removed" : ""}`}>
                  <span className="c-chk">
                    {gone ? null : <input type="checkbox" checked={picked.has(d.id)} onChange={() => toggle(d.id)} aria-label={`${fmtDay(d.date)} ${d.workTypeCode}`} />}
                  </span>
                  <span className="c-date">
                    {fmtDay(d.date)}
                    {d.sourcePhotoUrl ? (
                      <a href={d.sourcePhotoUrl} target="_blank" rel="noreferrer" className="kh-photo" onClick={(e) => e.stopPropagation()} title={t("Photo of the handwritten note this came from")}>
                        {t("Note photo")}
                      </a>
                    ) : null}
                  </span>
                  <span className="c-part">
                    {d.workTypeName ? t(d.workTypeName) : d.workTypeCode} <small>({d.workTypeCode})</small>
                    {d.waitingApproval ? <span className="kw-chip is-review ms-1">{t("Waiting for approval")}</span> : null}
                    {d.paid && !gone ? <span className="kw-chip ms-1">{t("Paid")}</span> : null}
                    {d.moveWaiting && !gone ? <span className="kw-chip is-review ms-1">{t("Move waiting for approval")}</span> : null}
                  </span>
                  <span className="num c-in">{d.inches} in</span>
                  <span className="c-who">
                    <span className="kw-code">{d.weaver?.code}</span> {d.weaver?.name}
                  </span>
                  <span className="c-help">{d.helper ? `+ ${d.helper.code} ${d.helper.name || ""}` : `(${t("alone")})`}</span>
                  {money ? <span className="num c-amt">{d.amount ? rupees(d.amount) : "—"}</span> : null}
                  <span className="c-edit">
                    {gone ? (
                      (d.canRestore ?? isOwner) ? (
                        <button type="button" className="kh-btn kh-btn--sm" onClick={() => restore(d)}>
                          {t("Restore")}
                        </button>
                      ) : null
                    ) : canCorrect && d.canChange === false ? null : (
                      <button
                        type="button"
                        className="kh-btn kh-btn--sm"
                        onClick={(e) => {
                          e.preventDefault(); // the row is a checkbox label
                          if (canCorrect) setChange({ kind: "edit", entry: d });
                          else setEdit({ id: d.id, date: d.date, weaverId: d.weaver?.id || "", helperId: d.helper?.id || "", inches: String(d.inches ?? ""), workTypeId: d.workTypeId, ladkaSool: !!d.ladkaSool, soolTouched: false });
                        }}
                      >
                        {t("Edit")}
                      </button>
                    )}
                  </span>
                  {gone ? (
                    <span className="c-note">
                      {d.movedTo
                        ? t("Moved to {{day}} · saree {{n}}", { day: fmtDay(d.movedTo.date), n: d.movedTo.sareeNumber || s.sareeNumber })
                        : t("Removed")}
                      {d.removed?.byName ? ` · ${t("by {{name}}", { name: d.removed.byName })}` : ""}
                      {d.removed?.reason ? ` · “${d.removed.reason}”` : ""}
                      {d.removed?.paidAmount ? ` · ${t("paid {{amount}} comes off the next pay sheet", { amount: rupees(d.removed.paidAmount) })}` : ""}
                    </span>
                  ) : d.movedFrom ? (
                    <span className="c-note">{t("Moved from {{day}} · saree {{n}}", { day: fmtDay(d.movedFrom.date), n: d.movedFrom.sareeNumber || s.sareeNumber })}</span>
                  ) : null}
                </Row>
              );
            })}
            {!days.length ? <div className="kw-empty">{t("No work entered on this saree yet.")}</div> : null}
          </div>

          {(other.time || []).length || (other.jobs || []).length ? (
            <>
              <h3 className="kr-group-title">{t("Other work: warping, dyeing…")}</h3>
              <div className="kh-table">
                {(other.time || []).map((w: any, i: number) => (
                  <div key={`t${i}`} className="kh-table__row">
                    <span>{fmtDay(w.date)}</span>
                    <span>
                      {t(w.activity)}
                      {w.taggedTo === "LOOM" ? ` (${t("loom")} ${w.loomCode})` : ""}
                    </span>
                    <span>{hours(w.minutes || 0)}</span>
                    <span>{w.person}</span>
                    {money ? <span className="num">{w.amount ? rupees(w.amount) : "—"}</span> : null}
                  </div>
                ))}
                {(other.jobs || []).map((j: any, i: number) => (
                  <div key={`j${i}`} className="kh-table__row">
                    <span>{fmtDay(j.date)}</span>
                    <span>
                      {t(j.activity)} · {j.quantity} {j.unit?.toLowerCase()}
                    </span>
                    <span>{j.note || ""}</span>
                    <span>{j.person}</span>
                    {money ? <span className="num" /> : null}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {(data.changes || []).length ? (
            <>
              <h3 className="kr-group-title">{t("Changes")}</h3>
              <ChangeList changes={data.changes} />
            </>
          ) : null}

          {(data.sheetHistory || []).length ? (
            <>
              <h3 className="kr-group-title">{t("Earlier, from the old sheet")}</h3>
              <div className="kh-table">
                {data.sheetHistory.map((r: any, i: number) => (
                  <div key={i} className="kh-table__row">
                    <span>{r.date ? fmtDay(r.date) : "—"}</span>
                    <span>{r.workTypeCode}</span>
                    <span className="num">{r.inches}</span>
                    <span>{r.weaver}</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </>
      ) : null}
      <EntryChangeDialog
        target={change}
        sareeNumber={s?.sareeNumber ? String(s.sareeNumber) : ""}
        isOwner={isOwner}
        roster={roster}
        types={types}
        onClose={() => setChange(null)}
        onDone={() => {
          setPicked(new Set());
          load();
        }}
      />
      <Modal isOpen={!!edit} toggle={() => setEdit(null)} centered>
        <ModalHeader toggle={() => setEdit(null)}>{edit ? t("Correct the entry for {{day}}", { day: fmtDay(edit.date) }) : ""}</ModalHeader>
        {edit ? (
          <ModalBody>
            <label className="kr-label" htmlFor="ke-weaver">
              {t("Weaver")}
            </label>
            <select id="ke-weaver" className="kr-select" value={edit.weaverId} onChange={(e) => setEdit({ ...edit, weaverId: e.target.value })}>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} {p.name}
                </option>
              ))}
            </select>
            <label className="kr-label mt-2" htmlFor="ke-helper">
              {t("Helper")}
            </label>
            <select id="ke-helper" className="kr-select" value={edit.helperId} onChange={(e) => setEdit({ ...edit, helperId: e.target.value })}>
              <option value="">{t("Alone (no helper)")}</option>
              {roster.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} {p.name}
                </option>
              ))}
            </select>
            <label className="kr-label mt-2" htmlFor="ke-part">
              {t("Part")}
            </label>
            <select id="ke-part" className="kr-select" value={edit.workTypeId} onChange={(e) => setEdit({ ...edit, workTypeId: e.target.value })}>
              {types.map((w) => (
                <option key={w.id} value={w.id}>
                  {partName(w, lang, t)} ({w.code})
                </option>
              ))}
            </select>
            <label className="kr-label mt-2" htmlFor="ke-inches">
              {t("Inches")}
            </label>
            <input id="ke-inches" className="kr-select" inputMode="decimal" value={edit.inches} onChange={(e) => setEdit({ ...edit, inches: e.target.value.replace(/[^\d.]/g, "") })} />
            <label className="kr-label mt-2" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={edit.ladkaSool} onChange={(e) => setEdit({ ...edit, ladkaSool: e.target.checked, soolTouched: true })} />
              {t("Woven with ladka sool")}
            </label>
          </ModalBody>
        ) : null}
        <ModalFooter>
          <button type="button" className="kh-btn" onClick={() => setEdit(null)}>
            {t("Cancel")}
          </button>
          <button type="button" className="kh-btn is-primary" disabled={busy || !edit?.weaverId || !(Number(edit?.inches) > 0)} onClick={saveEdit}>
            {busy ? t("Saving…") : t("Save")}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaSareeHistoryPage;
